import { useCallback, useEffect, useMemo, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory, useParams } from "react-router-dom";
import { produce } from "immer";

import { ActionBar } from "../../components/ActionBar";
import { Button, IconButton } from "../../components/Button";
import { ConfirmSheet } from "../../components/ConfirmSheet";
import { AreaField, Field, FieldGrid, FieldSet, Toggle } from "../../components/Fields";
import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { DiscloseRow, NavRow, RowGroup } from "../../components/Rows";
import { Screen } from "../../components/Shell";
import { Tag } from "../../components/Tag";
import { TopBar } from "../../components/TopBar";
import { cycleInfo } from "../../domain/cycle";
import { periodsStopped } from "../../domain/eating";
import { parseDay, todayStr } from "../../domain/date";
import { bmi, curWeight, dayKcal } from "../../domain/energy";
import { level } from "../../domain/progress";
import { stats } from "../../domain/stats";
import type { KayaState } from "../../domain/types";
import { hydrate } from "../../store/defaults";
import { setCycleSettings, setPeriodStart } from "../../store/toggles";
import { DEFAULT_STEP_GOAL } from "../../domain/activity";
import { clamp, numOr } from "../settings/profileForm";
import { useIsAdmin, useKaya } from "../../store/kayaStore";
import { api, type AdminUserDetail as Detail, type UserStatus } from "../../sync/api";
import { UserViewNotice } from "./UserViewNotice";
import "../You.css";

/** "Wed 23 Sep 2026" — the year kept, because these lists reach back further than one. */
function pretty(ds: string): string {
  return parseDay(ds).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function agoText(ms: number | null | undefined): string {
  if (!ms) return "never";
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
  const d = Math.floor(mins / (24 * 60));
  return d === 1 ? "yesterday" : `${d} days ago`;
}

const STATUS_TAG: Record<UserStatus, "measured" | "planned" | "est"> = {
  approved: "measured",
  pending: "planned",
  suspended: "est",
};

/**
 * The numeric fields, held as text for as long as they are being typed.
 *
 * They cannot be bound straight to the records. Validating a number on every keystroke means the
 * "1" on the way to "1993" fails the range check, and a field whose displayed value comes from the
 * validated record then empties itself — the year of birth could not be typed at all, and a
 * clamped cycle length jumped to 20 the moment you pressed 3. Text in, one validation pass at
 * save, exactly as `pages/settings/profileForm.ts` does it for the owner's own screen.
 */
interface Nums {
  name: string;
  heightCm: string;
  startWeight: string;
  targetWeight: string;
  birthYear: string;
  stepGoal: string;
  cycleLength: string;
  periodLen: string;
}

function seedNums(S: KayaState): Nums {
  const p = S.profile;
  return {
    name: p.name,
    heightCm: String(p.heightCm),
    startWeight: String(p.startWeight),
    targetWeight: String(p.targetWeight),
    birthYear: p.birthYear ? String(p.birthYear) : "",
    stepGoal: String(p.stepGoal),
    cycleLength: String(S.cycle.length),
    periodLen: String(S.cycle.periodLen),
  };
}

/** Folds the typed text back into the records, applying the same rules the owner's own form
 *  applies — an admin must not be able to write a value their screen would refuse. */
function applyNums(S: KayaState, n: Nums): KayaState {
  return produce(S, (d) => {
    const q = d.profile;
    q.name = n.name.trim();
    q.heightCm = numOr(n.heightCm, q.heightCm);
    q.startWeight = numOr(n.startWeight, q.startWeight);
    q.targetWeight = n.targetWeight ? Number(n.targetWeight) : 0;
    q.stepGoal = clamp(numOr(n.stepGoal, DEFAULT_STEP_GOAL), 500, 50000);

    // A year rather than an age, and cleared rather than kept at a nonsense value.
    const by = Math.round(numOr(n.birthYear, 0));
    if (by >= 1900 && by <= new Date().getFullYear() - 5) q.birthYear = by;
    else delete q.birthYear;

    setCycleSettings(d, {
      length: clamp(numOr(n.cycleLength, 28), 20, 45),
      periodLen: clamp(numOr(n.periodLen, 5), 2, 8),
    });
  });
}

/**
 * Person: one account and one person's records, on one screen.
 *
 * The structured sections cover what actually goes wrong — a wrong weight, a period logged on the
 * wrong day, a session that did not save — and the raw editor at the bottom covers everything
 * else rather than pretending forty forms exist. Every records edit is held locally until Save
 * changes, so a half-finished change never reaches the phone it belongs to; the account's own
 * details, password, status and deletion act at once and say so.
 */
export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { user: me, showToast } = useKaya();
  const isAdmin = useIsAdmin();

  const [detail, setDetail] = useState<Detail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Deleting an account asks twice, as it always did: once for what goes, once that it is final.
  const [askDelete, setAskDelete] = useState<0 | 1 | 2>(0);
  const [busy, setBusy] = useState(false);

  /** The state blob as it is being edited. `null` until loaded; hydrated so a sparse blob from an
   *  older build still has every collection the editors below touch. */
  const [draft, setDraft] = useState<KayaState | null>(null);
  const [nums, setNums] = useState<Nums | null>(null);
  const [dirty, setDirty] = useState(false);
  const [raw, setRaw] = useState("");
  const [account, setAccount] = useState({ name: "", email: "" });
  const [password, setPassword] = useState("");
  const [newStart, setNewStart] = useState(todayStr());

  const load = useCallback(async () => {
    try {
      const d = await api.adminUser(id);
      setDetail(d);
      setAccount({ name: d.user.name, email: d.user.email });
      const S = hydrate(d.state);
      setDraft(S);
      setNums(seedNums(S));
      setRaw(JSON.stringify(S, null, 2));
      setDirty(false);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load that account");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const edit = (fn: (d: KayaState) => void) => {
    setDraft((cur) => (cur ? produce(cur, fn) : cur));
    setDirty(true);
  };

  const setNum = (k: keyof Nums, v: string) => {
    setNums((cur) => (cur ? { ...cur, [k]: v } : cur));
    setDirty(true);
  };

  /** The records as they would be saved: the structural edits, with the typed text folded in.
   *  Everything below reads this rather than `draft`, so the figures move as you type. */
  const pending = useMemo(() => (draft && nums ? applyNums(draft, nums) : null), [draft, nums]);

  const st = useMemo(() => (pending ? stats(pending) : null), [pending]);
  const lv = useMemo(() => (pending ? level(pending) : null), [pending]);
  const ci = useMemo(() => (pending ? cycleInfo(pending) : null), [pending]);

  async function saveState() {
    if (!pending) return;
    setBusy(true);
    try {
      await api.adminPutState(id, pending);
      showToast("Records saved — their phone picks it up on the next sync");
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  async function patch(p: { name?: string; email?: string; status?: UserStatus; role?: "user" | "admin" }) {
    setBusy(true);
    try {
      await api.adminPatchUser(id, p);
      showToast("Account updated");
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not update the account");
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (password.length < 8) {
      showToast("Password needs at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.adminSetPassword(id, password);
      setPassword("");
      showToast("Password set — tell them what it is");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not set the password");
    } finally {
      setBusy(false);
    }
  }

  async function removeAccount() {
    if (!detail) return;
    setAskDelete(0);
    setBusy(true);
    try {
      await api.adminDeleteUser(id);
      showToast("Account deleted");
      history.replace("/you/people");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not delete the account");
      setBusy(false);
    }
  }

  function applyRaw() {
    try {
      const parsed: unknown = JSON.parse(raw);
      const cand = parsed as Partial<KayaState>;
      if (!cand || typeof cand !== "object" || !cand.profile || !cand.logs)
        throw new Error("not a state blob");
      const S = hydrate(cand);
      setDraft(S);
      // Reseed, or the text still in the boxes would be folded back over what was just pasted.
      setNums(seedNums(S));
      setDirty(true);
      showToast("Loaded into the editor — still needs saving");
    } catch {
      showToast("That is not valid Kaya state");
    }
  }

  // One IonPage for this route in every state. Returning a different one for the error, the
  // loading spinner or the user-view notice left the outlet holding a page it no longer tracked,
  // which is how the notice ended up stranded on top of the settings hub.
  const back = { to: "/you/people", label: "Back to People" };
  if (!isAdmin || err || !detail || !draft || !nums || !pending || !st || !lv)
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <TopBar title="Person" back={back} />
            <div className="you-page">
              {!isAdmin ? (
                <UserViewNotice />
              ) : err ? (
                <Notice tone="danger" primary={{ label: "Try again", onClick: () => void load() }}>
                  {err}
                </Notice>
              ) : (
                <Hero label="Account" title="Loading…" />
              )}
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );

  const u = detail.user;
  const isMe = u.id === me?.id;
  const who = u.name || u.email;
  const measures = [...pending.measures].sort((a, b) => (a.date < b.date ? 1 : -1));
  const days = Object.keys(pending.logs).sort().reverse().slice(0, 60);
  const starts = [...(pending.cycle.starts || [])].sort().reverse();
  const weight = curWeight(pending);

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Person" back={back} />
          <div className="you-page">
            {/* the answer: who this is, whether they are in, and whether they are actually using it */}
            <Hero
              ariaLabel={who}
              label="Account"
              title={who}
              line={u.email}
              sub={
                <>
                  <Tag tone={STATUS_TAG[u.status]}>{u.status}</Tag>{" "}
                  last sync {agoText(u.lastSyncAt)} · {Object.keys(pending.logs).length} days logged
                </>
              }
              actions={
                u.status !== "approved" ? (
                  <Button icon={I.check} busy={busy} onClick={() => void patch({ status: "approved" })}>
                    {`Approve ${who.split(/[\s@]/)[0]}`}
                  </Button>
                ) : undefined
              }
            />

            <RowGroup heading="Their records">
              <DiscloseRow icon={I.progress} title="Their numbers" value={`Level ${lv.n}`}>
                <table className="you-table">
                  <tbody>
                    <tr>
                      <th scope="row">Weight</th>
                      <td>
                        {weight.toFixed(1)} kg · {st.lost >= 0 ? "−" : "+"}
                        {Math.abs(st.lost).toFixed(1)} kg
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">BMI</th>
                      <td>{bmi(pending, weight).toFixed(1)}</td>
                    </tr>
                    <tr>
                      <th scope="row">Streak</th>
                      <td>
                        {st.streak} days · longest {st.longest}
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Level</th>
                      <td>
                        {lv.n} · {lv.xp.toLocaleString()} XP
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Sessions finished</th>
                      <td>{st.sessions}</td>
                    </tr>
                    <tr>
                      <th scope="row">Treadmill walks</th>
                      <td>{st.treadmills}</td>
                    </tr>
                    <tr>
                      <th scope="row">Badges</th>
                      <td>{Object.keys(pending.badges).length}</td>
                    </tr>
                  </tbody>
                </table>
              </DiscloseRow>

              <DiscloseRow icon={I.calendar} title="Logged days" value={`${days.length} shown`}>
                <div className="you-disclose-stack">
                  <p>
                    The last sixty days with anything against them, newest first. Removing one
                    deletes that day&rsquo;s session, treadmill and walks.
                  </p>
                  {days.length ? (
                    <div className="k-rows">
                      {days.map((ds) => {
                        const L = pending.logs[ds];
                        return (
                          <NavRow
                            key={ds}
                            kind="action"
                            title={pretty(ds)}
                            subtitle={
                              (L.completedAt ? "session done" : "started") +
                              (L.treadmill ? " · treadmill" : "") +
                              (L.deviceSteps ? ` · ${L.deviceSteps.toLocaleString()} steps` : "") +
                              ` · ~${Math.round(dayKcal(pending, ds))} kcal est.`
                            }
                            action={
                              <IconButton
                                icon={I.trash}
                                label={`Remove ${pretty(ds)}`}
                                onClick={() =>
                                  edit((d) => {
                                    delete d.logs[ds];
                                  })
                                }
                              />
                            }
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <p>Nothing logged.</p>
                  )}
                </div>
              </DiscloseRow>

              <DiscloseRow icon={I.scale} title="Measurements" value={`${measures.length} logged`}>
                <div className="you-disclose-stack">
                  {measures.length ? (
                    <div className="k-rows">
                      {measures.map((m) => (
                        <NavRow
                          key={m.date}
                          kind="action"
                          title={pretty(m.date)}
                          subtitle={[m.w ? `${m.w} kg` : "", m.waist ? `${m.waist} cm waist` : ""]
                            .filter(Boolean)
                            .join(" · ")}
                          action={
                            <IconButton
                              icon={I.trash}
                              label={`Remove the measurement from ${pretty(m.date)}`}
                              onClick={() =>
                                edit((d) => {
                                  d.measures = d.measures.filter((x) => x.date !== m.date);
                                })
                              }
                            />
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <p>Nothing logged.</p>
                  )}
                </div>
              </DiscloseRow>

              <DiscloseRow icon={I.footsteps} title="Step uploads" value={`${detail.steps.length} days`}>
                <div className="you-disclose-stack">
                  {detail.steps.length ? (
                    <div className="k-rows">
                      {detail.steps.slice(0, 30).map((s) => (
                        <NavRow
                          key={s.date}
                          kind="value"
                          title={pretty(s.date)}
                          subtitle={s.source}
                          value={s.steps.toLocaleString()}
                        />
                      ))}
                    </div>
                  ) : (
                    <p>No phone has posted steps for this account.</p>
                  )}
                  <p>
                    The 30 most recent of {detail.steps.length} kept. Step days live outside the
                    records and are read-only here — the phone writes them and would overwrite an
                    edit on its next upload.
                  </p>
                </div>
              </DiscloseRow>

              <DiscloseRow
                icon={I.drop}
                tone="cycle"
                title="Cycle"
                value={
                  periodsStopped(pending)
                    ? "Periods have stopped"
                    : pending.profile.cycleOn
                      ? ci?.day
                        ? `Day ${ci.day}`
                        : "On"
                      : "Off"
                }
              >
                <div className="you-disclose-stack">
                  {pending.profile.cycleOn ? (
                    <>
                      <FieldGrid>
                        <Field
                          label="Cycle length"
                          unit="days"
                          type="number"
                          value={nums.cycleLength}
                          onChange={(v) => setNum("cycleLength", v)}
                        />
                        <Field
                          label="Period length"
                          unit="days"
                          type="number"
                          value={nums.periodLen}
                          onChange={(v) => setNum("periodLen", v)}
                        />
                      </FieldGrid>
                      {starts.length ? (
                        <div className="k-rows">
                          {starts.map((ds) => (
                            <NavRow
                              key={ds}
                              kind="action"
                              title={pretty(ds)}
                              subtitle={
                                pending.cycle.ends?.[ds] ? `ended ${pretty(pending.cycle.ends[ds])}` : "no end"
                              }
                              action={
                                <IconButton
                                  icon={I.trash}
                                  label={`Remove ${pretty(ds)}`}
                                  onClick={() => edit((d) => setPeriodStart(d, ds, false))}
                                />
                              }
                            />
                          ))}
                        </div>
                      ) : (
                        <p>No periods logged.</p>
                      )}
                      <FieldGrid>
                        <Field label="Period start" type="date" value={newStart} onChange={setNewStart} />
                        <div className="you-actions you-actions--end">
                          <Button
                            variant="ghost"
                            icon={I.plus}
                            disabled={!/^\d{4}-\d{2}-\d{2}$/.test(newStart)}
                            onClick={() => edit((d) => setPeriodStart(d, newStart, true))}
                          >
                            Add start
                          </Button>
                        </div>
                      </FieldGrid>
                    </>
                  ) : (
                    <p>{u.name || "This account"} does not track a cycle.</p>
                  )}
                </div>
              </DiscloseRow>

              <DiscloseRow icon={I.edit} title="Their profile" value={nums.name || "No name"}>
                <div className="you-disclose-stack">
                  <FieldGrid>
                    <Field label="Name" type="text" value={nums.name} onChange={(v) => setNum("name", v)} />
                    <Field
                      label="Height"
                      unit="cm"
                      type="number"
                      value={nums.heightCm}
                      onChange={(v) => setNum("heightCm", v)}
                    />
                    <Field
                      label="Start weight"
                      unit="kg"
                      type="number"
                      step="0.1"
                      value={nums.startWeight}
                      onChange={(v) => setNum("startWeight", v)}
                    />
                    <Field
                      label="Target weight"
                      unit="kg"
                      type="number"
                      step="0.1"
                      value={nums.targetWeight}
                      onChange={(v) => setNum("targetWeight", v)}
                    />
                    <Field
                      label="Birth year"
                      type="number"
                      value={nums.birthYear}
                      hint="Blank clears it"
                      onChange={(v) => setNum("birthYear", v)}
                    />
                    <Field
                      label="Step goal"
                      type="number"
                      value={nums.stepGoal}
                      onChange={(v) => setNum("stepGoal", v)}
                    />
                  </FieldGrid>
                  <Toggle
                    title="Has a treadmill"
                    sub="Off hides the interval protocol and machine programs; free walks stay. Nothing logged is removed."
                    on={pending.profile.hasTreadmill !== false}
                    onChange={(v) =>
                      edit((d) => {
                        d.profile.hasTreadmill = v;
                      })
                    }
                  />
                </div>
              </DiscloseRow>

              <DiscloseRow icon={I.user} title="Account" value="saves at once">
                <div className="you-disclose-stack">
                  <FieldSet legend="Details">
                    <Field
                      label="Name"
                      type="text"
                      value={account.name}
                      onChange={(v) => setAccount({ ...account, name: v })}
                    />
                    <Field
                      label="Email"
                      type="email"
                      value={account.email}
                      onChange={(v) => setAccount({ ...account, email: v })}
                    />
                    <div className="you-actions">
                      <Button
                        variant="ghost"
                        disabled={busy || (account.name === u.name && account.email === u.email)}
                        onClick={() => void patch({ name: account.name, email: account.email })}
                      >
                        Save details
                      </Button>
                    </div>
                  </FieldSet>

                  <FieldSet
                    legend="Password"
                    hint="Sets a new password without the old one. They are not told — you have to."
                  >
                    {/* "new-password" so the browser does not offer to file somebody else's password
                        away in the admin's own password manager. */}
                    <Field
                      label="New password"
                      type="password"
                      value={password}
                      autoComplete="new-password"
                      onChange={setPassword}
                      hint="At least 8 characters"
                    />
                    <div className="you-actions">
                      <Button
                        variant="ghost"
                        disabled={busy || password.length < 8}
                        onClick={() => void resetPassword()}
                      >
                        Set password
                      </Button>
                    </div>
                  </FieldSet>

                  <p>
                    This account is {u.role === "admin" ? "an admin" : "a normal user"}. Admin is
                    decided by ADMIN_EMAILS on the server and reapplied at every sign-in.
                  </p>

                  <div className="you-actions">
                    <Button
                      variant="danger"
                      icon={I.trash}
                      disabled={busy || isMe}
                      onClick={() => setAskDelete(1)}
                    >
                      {`Delete ${who}'s account`}
                    </Button>
                  </div>
                  {isMe ? <p>You cannot delete your own account here.</p> : null}
                </div>
              </DiscloseRow>

              <DiscloseRow icon={I.list} title="Raw records" value="JSON">
                <div className="you-disclose-stack">
                  <p>
                    The whole state. Everything the sections above do not cover lives here — paste an
                    edited copy, load it into the editor, then Save changes.
                  </p>
                  <AreaField label="State JSON" value={raw} rows={14} onChange={setRaw} />
                  <div className="you-actions">
                    <Button variant="ghost" onClick={applyRaw}>
                      Load into the editor
                    </Button>
                    <Button variant="ghost" onClick={() => setRaw(JSON.stringify(pending, null, 2))}>
                      Reset to current
                    </Button>
                  </div>
                </div>
              </DiscloseRow>
            </RowGroup>

            <p className="you-fine">
              Edits wait for Save changes. Saving stamps the records as changed now, so their phone
              takes this copy on its next sync rather than quietly putting its own back.
            </p>

            <RowGroup heading="Access">
              {u.status !== "pending" ? (
                <NavRow
                  kind="action"
                  icon={I.clock}
                  title="Move back to pending"
                  subtitle="They see the waiting screen"
                  action={
                    <Button
                      variant="ghost"
                      disabled={busy || isMe}
                      onClick={() => void patch({ status: "pending" })}
                    >
                      Move
                    </Button>
                  }
                />
              ) : null}
              {u.status === "approved" ? (
                isMe ? (
                  <NavRow kind="disabled" icon={I.lock} title="Suspend this account" subtitle="You cannot suspend yourself" />
                ) : (
                  <NavRow
                    kind="danger"
                    icon={I.lock}
                    title={`Suspend ${who}'s account`}
                    subtitle="Keeps the data, blocks sign-in"
                    onClick={() => {
                      if (!busy) void patch({ status: "suspended" });
                    }}
                  />
                )
              ) : null}
            </RowGroup>
          </div>

          <ActionBar
            primary={{
              label: "Save changes",
              icon: I.check,
              onClick: () => void saveState(),
              disabled: !dirty,
              reason: "No changes yet",
              busy: busy && dirty,
            }}
            secondary={dirty ? { label: "Discard", onClick: () => void load() } : undefined}
          />
          {askDelete && detail ? (
            <ConfirmSheet
              title={
                askDelete === 1
                  ? `Delete ${detail.user.name || detail.user.email}?`
                  : "This cannot be undone. Delete for good?"
              }
              sub={askDelete === 1 ? "Their logs, measurements and step history go too, permanently." : undefined}
              confirm={
                askDelete === 1
                  ? { label: "Delete account", onClick: () => setAskDelete(2) }
                  : { label: "Delete for good", onClick: () => void removeAccount(), busy }
              }
              onClose={() => setAskDelete(0)}
            />
          ) : null}
        </Screen>
      </IonContent>
    </IonPage>
  );
}
